export const PATTERNS = [
  "authority_gap",
  "accountability_failure",
  "dependency_loop",
  "structural_multi",
  "behavioral",
  "developing",
  "functional"
];

export const PATTERN_LABELS = {
  authority_gap: "Authority Gap",
  accountability_failure: "Accountability Failure",
  dependency_loop: "Dependency Loop",
  structural_multi: "Multiple Dimensions",
  behavioral: "Behavioural Pattern",
  developing: "Developing Gap",
  functional: "Largely Functional"
};

export const DIMENSIONS = [
  "DECISION",
  "ACCOUNTABILITY",
  "DEPENDENCY",
  "MARGIN"
];

export const DIMENSION_LABELS = {
  DECISION: "Decision Architecture",
  ACCOUNTABILITY: "Accountability Structure",
  DEPENDENCY: "Founder Dependency",
  MARGIN: "Margin Signal"
};

export function parseJSON(raw, fallback = {}) {
  if (!raw) return fallback;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export function normalizePattern(value) {
  const text = String(value || "").toLowerCase();
  if (text.includes("authority") || text.includes("decis")) return "authority_gap";
  if (text.includes("account")) return "accountability_failure";
  if (text.includes("depend") || text.includes("overcommit") || text.includes("loop")) return "dependency_loop";
  if (text.includes("multi")) return "structural_multi";
  if (text.includes("behav")) return "behavioral";
  if (text.includes("develop")) return "developing";
  if (text.includes("functional") || text.includes("low severity")) return "functional";
  return null;
}

export function scoreSeverity(overallPercent) {
  if (overallPercent >= 70) return "Critical";
  if (overallPercent >= 40) return "High";
  return "Moderate";
}

export function readableTime(ms) {
  if (!ms || ms <= 0) return "—";
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

export function quarterLabel(dateLike) {
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "Unknown";
  const q = Math.floor(date.getMonth() / 3) + 1;
  return `Q${q} ${date.getFullYear()}`;
}

export function formatPct(value) {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

export function toSubmissionRecord(row) {
  const answers = parseJSON(row.answers, {});
  const result = parseJSON(row.final_result || row.result, {});
  const telemetry = parseJSON(row.telemetry, {});
  
  // Backward compatibility check for v2.1 signature
  const isV2 = answers?.["15"] === "founder_bottleneck_audit_v2.1" || answers?.[15] === "founder_bottleneck_audit_v2.1";
  
  let revenue = "Unknown";
  if (!isV2) {
    revenue = answers?.[12] || answers?.["12"] || "Unknown";
  }

  // Dimension scores (mapped to patternScores)
  const patternScores = result?.dimensionScores || result?.patternScores || {};

  let primary = result?.bottleneckType;
  if (!primary && result?.primary?.name) {
    primary = normalizePattern(result.primary.name);
  }
  if (!primary) {
    primary = "functional";
  }

  let overallPercent = 0;
  if (result?.overall?.percent != null) {
    overallPercent = Number(result.overall.percent);
  } else if (result?.totalScore != null) {
    overallPercent = Math.round((Number(result.totalScore) / 36) * 100);
  }

  const events = Array.isArray(telemetry?.events) ? telemetry.events : [];
  return {
    ...row,
    answers,
    result,
    telemetry,
    events,
    patternScores,
    primary,
    overallPercent,
    completed: Boolean(result?.bottleneckType) || Boolean(result?.overall) || Boolean(telemetry?.completedAtMs),
    createdAt: row.created_at,
    revenue,
    email: row.email || row.user_email || "unknown",
  };
}

export function computeAnalytics(submissions) {
  const total = submissions.length;
  const completedRows = submissions.filter((row) => row.completed);
  const completionRate = total ? completedRows.length / total : 0;

  const patternFrequency = PATTERNS.reduce((acc, p) => {
    acc[p] = 0;
    return acc;
  }, {});

  const severityDist = { Moderate: 0, High: 0, Critical: 0 };
  const pairCounts = {};
  const revenuePattern = {};
  const timeValues = [];
  const dropOff = {};

  let reportViewed = 0;
  let ctaClicks = 0;
  let bookingIntentWithinDay = 0;
  let bookingIntentBase = 0;
  let paidClients = 0;

  const conversionByPattern = {};
  const conversionByScoreBand = {
    "0-39": [0, 0],
    "40-69": [0, 0],
    "70-100": [0, 0],
  };
  const quarterPattern = {};
  const reactiveByRevenue = {};
  const emailHistory = {};

  completedRows.forEach((row) => {
    if (row.primary && patternFrequency[row.primary] !== undefined) {
      patternFrequency[row.primary] += 1;
    }

    severityDist[scoreSeverity(row.overallPercent)] += 1;

    // Co-occurrence based on high dimension scores (score >= 5)
    const presentDims = DIMENSIONS.filter((dim) => {
      const score = row.patternScores[dim] ?? row.patternScores[DIMENSION_LABELS[dim]] ?? 0;
      return score >= 5;
    });
    for (let i = 0; i < presentDims.length; i += 1) {
      for (let j = i + 1; j < presentDims.length; j += 1) {
        const pair = [presentDims[i], presentDims[j]].sort().join(" + ");
        pairCounts[pair] = (pairCounts[pair] || 0) + 1;
      }
    }

    const revenueKey = row.revenue;
    if (!revenuePattern[revenueKey]) {
      revenuePattern[revenueKey] = DIMENSIONS.reduce((acc, dim) => {
        acc[dim] = { sum: 0, count: 0 };
        return acc;
      }, {});
    }
    DIMENSIONS.forEach((dim) => {
      const score = row.patternScores[dim] ?? row.patternScores[DIMENSION_LABELS[dim]] ?? 0;
      revenuePattern[revenueKey][dim].sum += score;
      revenuePattern[revenueKey][dim].count += 1;
    });

    if (row.telemetry?.totalTimeMs) timeValues.push(Number(row.telemetry.totalTimeMs));

    const lastSeen = Number(row.telemetry?.lastQuestionIndexSeen);
    if (!row.completed && Number.isFinite(lastSeen)) {
      const label = `Q${lastSeen + 1}`;
      dropOff[label] = (dropOff[label] || 0) + 1;
    }

    const hasReportView = row.events.some(
      (evt) =>
        evt.type === "report_download_click" || evt.type === "report_download_finished"
    );
    const hasCta = row.events.some((evt) => evt.type === "cta_click");
    const hasPaid = row.events.some((evt) => evt.type === "paid_client");
    if (hasReportView) reportViewed += 1;
    if (hasCta) ctaClicks += 1;
    if (hasPaid) paidClients += 1;

    if (hasReportView) {
      bookingIntentBase += 1;
      const reportTs =
        row.events.find((evt) => evt.type === "report_download_finished")?.atMs ||
        row.events.find((evt) => evt.type === "report_download_click")?.atMs;
      const ctaTs = row.events.find((evt) => evt.type === "cta_click")?.atMs;
      if (reportTs && ctaTs && ctaTs - reportTs <= 24 * 3600 * 1000) {
        bookingIntentWithinDay += 1;
      }
    }

    const conversionSuccess = hasPaid || hasCta;
    if (row.primary) {
      if (!conversionByPattern[row.primary]) conversionByPattern[row.primary] = [0, 0];
      conversionByPattern[row.primary][0] += conversionSuccess ? 1 : 0;
      conversionByPattern[row.primary][1] += 1;
    }
    const band =
      row.overallPercent >= 70 ? "70-100" : row.overallPercent >= 40 ? "40-69" : "0-39";
    conversionByScoreBand[band][0] += conversionSuccess ? 1 : 0;
    conversionByScoreBand[band][1] += 1;

    const quarter = quarterLabel(row.createdAt);
    if (!quarterPattern[quarter]) {
      quarterPattern[quarter] = PATTERNS.reduce((acc, p) => {
        acc[p] = 0;
        return acc;
      }, {});
    }
    if (row.primary && quarterPattern[quarter][row.primary] !== undefined) {
      quarterPattern[quarter][row.primary] += 1;
    }

    if (!reactiveByRevenue[revenueKey]) {
      reactiveByRevenue[revenueKey] = { reactive: 0, total: 0 };
    }
    reactiveByRevenue[revenueKey].total += 1;
    // Map "dependency_loop" as our new proxy indicator for high constraint
    if (row.primary === "dependency_loop") {
      reactiveByRevenue[revenueKey].reactive += 1;
    }

    if (!emailHistory[row.email]) emailHistory[row.email] = [];
    emailHistory[row.email].push({
      createdAt: row.createdAt,
      overallPercent: row.overallPercent,
    });
  });

  const averageTimeMs = timeValues.length
    ? Math.round(timeValues.reduce((sum, t) => sum + t, 0) / timeValues.length)
    : 0;

  const retakeEmails = Object.values(emailHistory).filter((list) => list.length > 1);
  const repeatAssessments = retakeEmails.map((list) => {
    const sorted = [...list].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    return {
      first: sorted[0].overallPercent,
      latest: sorted[sorted.length - 1].overallPercent,
    };
  });
  const repeatDelta = repeatAssessments.length
    ? (
        repeatAssessments.reduce((sum, row) => sum + (row.latest - row.first), 0) /
        repeatAssessments.length
      ).toFixed(1)
    : "0.0";

  const started = total;
  const completed = completedRows.length;
  const funnel = {
    started,
    completed,
    reportViewed,
    ctaClicked: ctaClicks,
    paid: paidClients,
  };

  return {
    total,
    completed,
    completionRate,
    patternFrequency,
    severityDist,
    topPairs: Object.entries(pairCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8),
    pairCounts,
    revenuePattern,
    averageTimeMs,
    dropOff,
    reportViewedRate: completedRows.length ? reportViewed / completedRows.length : 0,
    ctaRate: completedRows.length ? ctaClicks / completedRows.length : 0,
    reportToBookingIntentRate: bookingIntentBase
      ? bookingIntentWithinDay / bookingIntentBase
      : 0,
    reportToPaidRate: reportViewed ? paidClients / reportViewed : 0,
    conversionByPattern,
    conversionByScoreBand,
    quarterPattern,
    reactiveByRevenue,
    retakeRate: submissions.length ? retakeEmails.length / submissions.length : 0,
    repeatDelta,
    funnel,
  };
}

export function applyDashboardFilters(submissions, { timeframe, revenue }) {
  let rows = submissions;
  const now = Date.now();
  if (timeframe === "7d") {
    const c = now - 7 * 86400000;
    rows = rows.filter((r) => new Date(r.createdAt).getTime() >= c);
  } else if (timeframe === "30d") {
    const c = now - 30 * 86400000;
    rows = rows.filter((r) => new Date(r.createdAt).getTime() >= c);
  } else if (timeframe === "90d") {
    const c = now - 90 * 86400000;
    rows = rows.filter((r) => new Date(r.createdAt).getTime() >= c);
  }
  if (revenue && revenue !== "all") {
    rows = rows.filter((r) => r.revenue === revenue);
  }
  return rows;
}
